// src/api/axios.ts
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

// eslint-disable-next-line
const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  '/api';

// 小ログ：環境変数が有効かどうかの確認
if (!API_BASE) {
  console.error('❌ API baseURL が未設定です。VITE_API_BASE または REACT_APP_API_BASE_URL を設定してください。');
} else {
  // 開発環境のみ表示
  if (
    typeof window !== 'undefined' &&
    window.location &&
    window.location.hostname.includes('localhost')
  ) {

  }
}

const api = axios.create({
  baseURL: API_BASE,
  // 本プロジェクトは Bearer Token を利用。CORS Cookie が必要な場合のみ true に変更。
  withCredentials: false,
  timeout: 20000,
});

// —— ユーティリティ：FormData 判定（JSON ヘッダを強制しないため）——
const isFormData = (data: any) => typeof FormData !== 'undefined' && data instanceof FormData;

// —— ユーティリティ：言語ヘッダ（後端のローカライズ用）——
// i18next は通常 localStorage.i18nextLng に言語を保持
const readLang = () => {
  const raw = localStorage.getItem('i18nextLng') || 'ja';
  return raw === 'zh' ? 'zh-CN' : raw.startsWith('ja') ? 'ja-JP' : raw;
};

// —— ユーティリティ：ログイン画面のパスを算出（現在の領域/役割に応じて）——
const computeLoginPath = () => {
  const path = window.location.pathname;
  const role = localStorage.getItem('role');
  if (path.startsWith('/doctor') || role === 'doctor') return '/login/doctor';
  if (path.startsWith('/admin') || role === 'admin') return '/login/admin';
  return '/login';
};

// 重複リダイレクト/重複アラート防止用フラグ
let isRedirecting = false;

// —— リクエストインターセプタ：常に最新トークンを付与 & 共通ヘッダ設定 —— 
api.interceptors.request.use((config) => {
  // 毎回 token を読み直し（凍結防止）
  const token = (localStorage.getItem('token') || '').replace(/^"|"$/g, '').trim();
  config.headers = config.headers ?? {};

  if (token) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  } else {
    delete (config.headers as any).Authorization;
  }

  // 言語ヘッダ（後端が必要な場合）
  (config.headers as any)['Accept-Language'] = readLang();
  (config.headers as any)['X-Requested-With'] = 'XMLHttpRequest';

  // FormData 以外は JSON ヘッダを既定適用
  if (!isFormData(config.data)) {
    (config.headers as any)['Content-Type'] = (config.headers as any)['Content-Type'] || 'application/json';
    (config.headers as any).Accept = (config.headers as any).Accept || 'application/json';
  }

  return config;
});

// —— 簡易 GET リトライ（ネットワーク断対策で 1 回だけ）——
const shouldRetry = (error: AxiosError, config?: AxiosRequestConfig) => {
  if (!config || (config as any).__retried) return false;
  // GET のみリトライ対象
  if ((config.method || 'get').toLowerCase() !== 'get') return false;
  // ネットワークエラー / 5xx のみリトライ
  const status = error.response?.status;
  return !status || (status >= 500 && status < 600);
};

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const { response, config } = error;

    // GET は 1 回だけ自動リトライ
    if (shouldRetry(error, config)) {
      (config as any).__retried = true;
      try {
        return await api.request(config!);
      } catch (e) {
        // 引き続き下のエラーハンドリングへ
      }
    }

    const status = response?.status;
    const detail: any = (response?.data as any)?.detail;
    const code: any = (response?.data as any)?.code;

    // 401：未認証/期限切れ → 資格情報を破棄し、該当ログイン画面へ
    if (status === 401 && !(config as any)?.skipAuthRedirect) {
      if (!isRedirecting) {
        isRedirecting = true;
        localStorage.removeItem('token');
        const role = localStorage.getItem('role');
        localStorage.removeItem('role');
        window.dispatchEvent(new Event('auth:changed'));

        if (!window.location.pathname.startsWith('/login')) {
          if (role === 'doctor' || role === 'admin') {
            // doctor/admin は自前ログインページへ（redirectTo パラメータ付き）
            // computeLoginPath() は role 削除後に呼ぶと null になるため、直接マップする
            const loginPath = role === 'doctor' ? '/login/doctor' : '/login/admin';
            const redirectTo = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = `${loginPath}?redirectTo=${redirectTo}`;
          } else {
            // 一般ユーザー：URL パラメータではなく sessionStorage でフラグを渡す
            // （LandingPage 側でフラグを読み取り、一度だけモーダルを表示する）
            sessionStorage.setItem('auth_expired', '1');
            sessionStorage.setItem('auth_expired_redirect', window.location.pathname + window.location.search);
            window.location.href = '/';
          }
        }
        setTimeout(() => (isRedirecting = false), 1500);
      }
      return Promise.reject(error);
    }

    // 403：権限エラー（BAN/ロール不一致など）
    if (status === 403) {
      const banned = code === 'ACCOUNT_BANNED' || code === 'USER_BANNED' || detail === 'USER_BANNED' || detail === 'ACCOUNT_BANNED';
      const invalidRole = code === 'INVALID_ROLE' || detail === 'INVALID_ROLE';

      if (banned) {
        alert('このアカウントは利用停止中です / This account has been banned.');
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        window.dispatchEvent(new Event('auth:changed'));
        if (!isRedirecting) {
          isRedirecting = true;
          const loginPath = computeLoginPath();
          window.location.href = loginPath;
          setTimeout(() => (isRedirecting = false), 1000);
        }
        return Promise.reject(error);
      }

      if (invalidRole) {
        alert('アカウント権限が不足しています / Invalid role.');
        if (!isRedirecting) {
          isRedirecting = true;
          // 一般ユーザー向けトップへ退避
          window.location.href = '/';
          setTimeout(() => (isRedirecting = false), 1000);
        }
        return Promise.reject(error);
      }

      // その他の 403
      if (detail === 'UNAUTHORIZED_USER') {
        // Suppress generic global alert/redirect so the specific UI can handle it.
        return Promise.reject(error);
      }

      alert('アクセス権限がありません / Access denied');
      if (!isRedirecting && !window.location.pathname.startsWith('/login')) {
        isRedirecting = true;
        window.location.href = '/';
        setTimeout(() => (isRedirecting = false), 1000);
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
